import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ChevronRight, BookOpen } from "lucide-react";
import { getProduct, checkout } from "@/lib/api";
import { PrimaryButton, Overline } from "@/components/site/Primitives";
import { CheckoutDialog } from "@/components/site/CheckoutDialog";
import { Reveal } from "@/lib/reveal";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => { getProduct(slug).then(setProduct).catch(() => {}); }, [slug]);

  const doCheckout = async (name, email) => {
    setLoading(true);
    try {
      const res = await checkout({ lookup_key: product.lookup_key, origin_url: window.location.origin, name, email });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open checkout. Please try again.");
      setLoading(false);
    }
  };

  if (!product) return <div className="min-h-[60vh] flex items-center justify-center font-serif text-2xl text-brand-muted">Gathering the pages…</div>;

  return (
    <div data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-8">
        <div className="flex items-center gap-2 font-sans text-[0.7rem] uppercase tracking-[0.14em] text-brand-muted">
          <Link to="/library" className="hover:text-brand-charcoal">Library</Link>
          <ChevronRight size={12} /><span className="text-brand-charcoal">{product.title}</span>
        </div>
      </div>

      {/* HERO SPLIT */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-20 grid lg:grid-cols-2 gap-16">
        <div className="bg-brand-warm-white border border-brand-linen p-8 md:p-14">
          <img src={product.cover} alt={product.title} className="w-full object-cover" />
        </div>
        <div className="lg:sticky lg:top-28 lg:self-start">
          <Overline>{product.category}</Overline>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light mt-4 mb-4 leading-tight">{product.title}</h1>
          <p className="font-body text-xl text-brand-muted italic mb-6">{product.subtitle}</p>
          <p className="font-body text-brand-body text-lg leading-relaxed mb-8">{product.description}</p>

          {/* DIGITAL EDITION */}
          <div className="mb-6">
            <div className="flex items-center gap-4 border border-brand-olive bg-brand-linen/40 p-5" data-testid="format-digital">
              <BookOpen size={22} className="text-brand-copper shrink-0" />
              <div className="flex-1">
                <span className="block font-sans text-[0.66rem] uppercase tracking-[0.12em] text-brand-charcoal">Digital Edition</span>
                <span className="block font-sans text-[0.6rem] uppercase tracking-[0.1em] text-brand-muted mt-1">Instant download</span>
              </div>
              <span className="font-serif text-2xl text-brand-charcoal">${product.price.toFixed(2)}</span>
            </div>
          </div>

          <PrimaryButton onClick={() => setCheckoutOpen(true)} testid="add-to-cart-button" disabled={loading}>
            {loading ? "Opening checkout…" : "Purchase & Download"}
          </PrimaryButton>
          <p className="font-sans text-[0.7rem] uppercase tracking-[0.14em] text-brand-muted mt-5">
            Instant digital delivery · Secure checkout
          </p>
        </div>
      </section>

      {/* FOREWORD PREVIEW */}
      {product.foreword && (
        <section className="max-w-3xl mx-auto px-6 py-16" data-testid="foreword-preview">
          <Reveal>
            <div className="border border-brand-linen bg-brand-warm-white p-10 md:p-14 relative">
              <span className="overline">Read the foreword — a free taste</span>
              <h3 className="font-serif text-3xl mt-3 mb-6">A Word Before We Begin</h3>
              <p className="font-body text-brand-body text-lg leading-loose first-letter:text-5xl first-letter:font-serif first-letter:float-left first-letter:mr-3 first-letter:leading-[0.8] first-letter:text-brand-olive">{product.foreword}</p>
              <p className="font-serif text-xl italic text-brand-olive mt-6 text-right">— Agnes</p>
            </div>
          </Reveal>
        </section>
      )}

      {/* STORY */}
      <section className="bg-brand-warm-white">
        <div className="max-w-3xl mx-auto px-6 py-24 md:py-32 text-center">
          <Reveal>
            <Overline>The story behind it</Overline>
            <blockquote className="font-serif text-3xl sm:text-4xl font-light leading-snug mt-6 mb-10 text-brand-charcoal">"{product.tagline}"</blockquote>
            <p className="font-body text-brand-body text-lg leading-loose text-left">{product.story}</p>
          </Reveal>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <Overline>Why you'll treasure it</Overline>
          <h2 className="text-4xl font-light mt-4 mb-8 leading-tight">A companion for a gentler home</h2>
          <ul className="space-y-5">
            {product.benefits.map((b, i) => (
              <li key={i} className="flex gap-4 items-start">
                <Check size={20} className="text-brand-sage mt-1 shrink-0" />
                <span className="font-body text-brand-body text-lg leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="bg-brand-linen/40 p-10">
            <Overline>What's included</Overline>
            <ul className="mt-6 space-y-4">
              {product.includes.map((inc, i) => (
                <li key={i} className="font-body text-brand-body text-lg border-b border-brand-linen pb-4 last:border-0">{inc}</li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      {/* TABLE OF CONTENTS */}
      <section className="bg-brand-olive text-brand-cream">
        <div className="max-w-4xl mx-auto px-6 py-24 md:py-32">
          <Reveal>
            <span className="overline text-brand-gold">Table of Contents</span>
            <h2 className="text-4xl sm:text-5xl font-light text-brand-cream mt-4 mb-12 leading-tight">Inside these pages</h2>
            <ol className="space-y-6">
              {product.toc.map((t, i) => (
                <li key={i} className="flex gap-6 items-baseline border-b border-brand-cream/20 pb-6">
                  <span className="font-serif text-3xl text-brand-gold w-12 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-body text-xl text-brand-cream/90">{t}</span>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      {/* PREVIEW PAGES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="preview-section">
        <Reveal><div className="text-center max-w-2xl mx-auto">
          <Overline>A glimpse inside</Overline>
          <h2 className="text-4xl sm:text-5xl font-light mt-4 mb-4 leading-tight">Preview pages</h2>
          <p className="font-body text-brand-body text-lg">Turn a few pages before it graces your own shelf.</p>
        </div></Reveal>
        <div className="grid md:grid-cols-3 gap-8 mt-16">
          {product.preview_pages.map((img, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <img src={img} alt={`Preview ${i + 1}`} className="w-full aspect-[3/4] object-cover border border-brand-linen" />
            </Reveal>
          ))}
        </div>
        <div className="text-center mt-16">
          <PrimaryButton onClick={() => setCheckoutOpen(true)} testid="preview-buy-button" disabled={loading}>Bring it home · ${product.price.toFixed(2)}</PrimaryButton>
        </div>
      </section>

      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} onConfirm={doCheckout} submitting={loading} title={`Buy ${product.title}`} />
    </div>
  );
}
